import type { Placement } from '@popperjs/core';
import type { Range } from '@tanstack/react-virtual';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import { Popover, useControllableState, useEffectOnValueChange } from '@timelyapp/react';
import classNames from 'classnames';
import type { Props as InputProps } from 'components/Input';
import Spinner from 'components/Spinner';
import type { ScrollbarsRefType } from 'components/scrollbars';
import { Scrollbars } from 'components/scrollbars';
import { EMPTY_ARRAY } from 'constants/defaults';
import type { ControllerStateAndHelpers, StateChangeOptions } from 'downshift';
import Downshift from 'downshift';
import memoizeOne from 'memoize-one';
import type { Key } from 'react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import FontAwesome from 'react-fontawesome';
import type { Tag } from 'types/tag';
import debug from 'utils/debug';
import { chainEvent } from 'utils/react';
import styles from './DropdownBuilder.module.scss';
import HourTags from './HourTags';
import { WarningDiamond } from './Icons';
import inputStyles from './Input.module.scss';
import InputDecorator from './InputDecorator';
import InputWithAutoFillBlocking from './InputWithAutoFillBlocking';
import { mergeRefs } from './popovers/utils';

const stateChangeTypes = Downshift.stateChangeTypes;

export interface SimpleOption<V> {
  label: string;
  value: V;
  sequence?: number | null | undefined;
  searchValue?: string | null | undefined; // Optional, fallback to name if falsy;
  isSelectable?: boolean | null | undefined;
}
export interface DropdownOption<V> extends SimpleOption<V> {
  disabled?: boolean | undefined;
  children?: Array<DropdownOption<V>> | null | undefined;
  /** Stable identity for keying rows whose `value` is a non-unique sentinel (e.g. -1). (TT-5164) */
  rowKey?: string | number | undefined;
}

export interface OnChangeMeta<V> {
  parentValue?: V;
}

interface RenderTagsProps {
  onClickTag?: (tagId: number) => void;
  selectedTag?: Tag;
  suggestedTags?: Array<Tag>;
}

interface BuilderContext<V, T extends DropdownOption<V>> {
  downshift: ControllerStateAndHelpers<RowNode<V, T>>;
  isOpen: boolean;
  hasItems: boolean;
  renderListPopover: (props?: RenderListPopoverProps) => React.ReactNode;
  renderList: (props?: RenderListProps) => React.ReactNode;
  renderInput: (props?: RenderInputProps) => React.ReactNode;
  renderTags: (props?: RenderTagsProps) => React.ReactNode;
}
export interface OptionMeta {
  isChild: boolean;
  isSelected: boolean;
  hasSelectedChild: boolean;
  isHighlighted: boolean;
}

export type OnDropdownChange<V, T> = (
  value: V | null,
  item: T | null,
  meta: OnChangeMeta<V>
) => unknown;

export type RenderOption<T> = (item: T, index: number, meta: OptionMeta) => React.ReactNode;
export interface DropdownBuilderProps<V, T extends DropdownOption<V>> {
  options: ReadonlyArray<T> | (() => ReadonlyArray<T>);
  value: V | null;
  values?: Array<V>;
  onChange: OnDropdownChange<V, T>;
  /** Keep dropdown open on change, useful for links (to support CMD+click) */
  clearable?: boolean;
  children?: (context: BuilderContext<V, T>) => React.ReactNode;
  isOpen?: boolean;
  popoverFixed?: boolean;
  onSetIsOpen?: (b: boolean) => void;
  onFocusChanged?: (b: boolean) => void;
  regularInput?: boolean;
  disabled?: boolean;
  rootOptionIsGroup?: boolean;
  postProcessOptions?: (
    options: Array<RowNode<V, T>>,
    filterString: string,
    addOption: (option: T) => void
  ) => void;
  // todo: Move index into meta
  renderOption?: RenderOption<T>;
  /** Customizes the native browser tooltip (title attribute) for each row option */
  getOptionTitle?: (option: T) => string | null | undefined;
  placeholder: string; // defaultProp'd,
  optionHeight: number; // defaultProp'd,
  inputCaret?: boolean; // defaultProp'd,
  inputIcon?: React.ReactNode;
  inputValidationError?: string;
  inlineInput?: boolean;
  matchOption?: (option: T, filterString: string) => boolean;
  inputActions?: React.ReactNode;
  stopEscPropagation?: boolean;
  filterString?: string;
  onSetFilterString?: (filterString: string) => void;
  // onLoadMoreOptions?: () => mixed,
  maxHeight?: number;
  // selectedOnTop?: boolean,
  highlightSelection: boolean; // defaultProp'd,
  minimumCharactersToSearchParent: number; // defaultProp'd,
  isLoadingOptions?: boolean;
  onOptionsRendered?: (options: Array<T>) => void;
  onClear?: () => void;
  inputRef?: React.Ref<HTMLInputElement>;
  noResultsLabel?: React.ReactNode;
}

const itemToString = <V, T extends DropdownOption<V>>(node: RowNode<V, T> | null) =>
  node?.option?.label || '';

const matchOptionDefault = <V, T extends DropdownOption<V>>(
  option: T,
  filterString?: string
): boolean => {
  const comparisonString = option.searchValue || option.label;
  if (!filterString?.length || !comparisonString?.length) {
    return true;
  }

  return comparisonString.toLocaleLowerCase().includes(filterString.toLocaleLowerCase());
};

const defaultOptionRenderer = <V, T extends DropdownOption<V>>(item: T) => (
  <span>{item?.label || ''}</span>
);

const defaultLayout = <V, T extends DropdownOption<V>>(context: BuilderContext<V, T>) => (
  <div className={styles.root}>
    {context.renderInput()}
    {context.isOpen && context.renderListPopover()}
  </div>
);

interface RowRendererProps {
  index: number;
  key: Key;
}

interface RenderListProps {
  width?: number | string;
  position?: Placement;
  className?: string;
}

interface RenderListPopoverProps {
  usePortal?: boolean;
  popoverPosition?: Placement;
  renderListProps?: RenderListProps;
  popoverListHeader?: React.ReactNode;
}

type RenderInputProps = InputProps;

export interface RowNode<V, T extends DropdownOption<V>> {
  option: T;
  selectable: boolean;
  selectableIndex: number;
  level: number;
  /** Identity of the parent group, so the same option under different groups gets distinct, stable keys. (TT-5200) */
  parentKey?: string | number | undefined;
}

/** Stable React/virtualizer key for a row; prefers an explicit rowKey, then the option value, falling back to index for object/nullish values. (TT-5164) */
export const rowNodeKey = <V, T extends DropdownOption<V>>(
  item: RowNode<V, T> | undefined,
  index: number
): string | number => {
  if (item == null) return index;
  // Scope children to their parent group: the same project can appear under "Recent Projects",
  // "Pinned Projects" AND its client group, and which copies survive a filter can differ (e.g.
  // searching "recent" keeps the Recent group via its header but drops the others). Parent-scoped
  // keys stay stable for the survivors. (TT-5200)
  const prefix = item.parentKey != null ? `${item.level}:${item.parentKey}` : `${item.level}`;
  if (item.option.rowKey != null) return `${prefix}:${item.option.rowKey}`;
  const value = item.option.value;
  if (value == null || typeof value === 'object') return index;
  // Prefix with level + type so a parent and child (or values of different types) can never collide.
  return `${prefix}:${typeof value}:${String(value)}`;
};

/**
 * Keys for ALL rows, guaranteed unique within the list. Parent-scoped `rowNodeKey` already keeps
 * cross-group duplicates apart; the occurrence suffix is a backstop for identical options under
 * the SAME parent (e.g. dirty paginated data listing a project twice under one client) — duplicate
 * React keys make the virtualizer's absolutely-positioned rows overlap. (TT-5200)
 */
export const uniqueRowKeys = <V, T extends DropdownOption<V>>(
  items: ReadonlyArray<RowNode<V, T>>
): Array<string | number> => {
  const seen = new Map<string | number, number>();
  return items.map((item, index) => {
    const key = rowNodeKey(item, index);
    const occurrence = seen.get(key) ?? 0;
    seen.set(key, occurrence + 1);
    return occurrence === 0 ? key : `${key}#${occurrence}`;
  });
};

const DropdownBuilder = <V, T extends DropdownOption<V>>({
  options,
  value,
  values,
  onChange,
  clearable,
  placeholder,
  optionHeight,
  renderOption,
  getOptionTitle,
  postProcessOptions,
  children,
  isOpen: isOpenFromProps,
  onSetIsOpen,
  onFocusChanged,
  disabled,
  inputCaret,
  inputIcon,
  inputValidationError,
  inlineInput,
  isLoadingOptions,
  matchOption,
  popoverFixed,
  inputActions,
  stopEscPropagation,
  rootOptionIsGroup,
  highlightSelection,
  minimumCharactersToSearchParent,
  maxHeight,
  regularInput,
  onOptionsRendered,
  onClear,
  inputRef,
  noResultsLabel = 'No results',
  ...props
}: DropdownBuilderProps<V, T>) => {
  const parentRef = useRef<ScrollbarsRefType | null>(null);

  const renderOptionWithFallback = renderOption || defaultOptionRenderer;
  const [filterString, setFilterStringInState] = useControllableState(
    props.filterString,
    props.onSetFilterString,
    ''
  );
  const [hasFocus, setHasFocusInState] = useState(false);
  const [isOpenInState, setIsOpenInState] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const isMultiSelect = values != null;

  const setHasFocus = useCallback(
    (b: boolean) => {
      setHasFocusInState(b);
      if (onFocusChanged) {
        onFocusChanged(b);
      }
    },
    [onFocusChanged]
  );
  const setIsOpen = useCallback(
    (o: boolean) => {
      // Allow controlling isOpen via props
      onSetIsOpen?.(o);

      // Only update state if we need to
      if (isOpenFromProps == null) {
        setIsOpenInState(o);
      }
    },
    [isOpenFromProps, onSetIsOpen]
  );
  const isOpen = isOpenFromProps != null ? isOpenFromProps : isOpenInState;
  const [hasBeenOpened, setHasBeenOpened] = useState(isOpen);

  useEffectOnValueChange(isOpen, () => {
    if (isOpen && !hasBeenOpened) {
      setHasBeenOpened(true);
    }
  });

  // This memoization seems good enough, no lag with 20k items
  const getItems = useMemo(
    () =>
      memoizeOne((filterString: string) => {
        const optionsToProcess =
          typeof options === 'function' ? (hasBeenOpened ? options() : EMPTY_ARRAY) : options;

        return processOptions(
          optionsToProcess,
          filterString,
          matchOption,
          isMultiSelect,
          rootOptionIsGroup,
          minimumCharactersToSearchParent,
          postProcessOptions
        );
      }),
    [
      options,
      matchOption,
      isMultiSelect,
      rootOptionIsGroup,
      minimumCharactersToSearchParent,
      postProcessOptions,
      hasBeenOpened,
    ]
  );

  const setFilterString = useCallback(
    (newFilterString: string) => {
      setFilterStringInState(newFilterString);
      const { items: newItems } = getItems(newFilterString);
      // Make sure implicitly filtered parents do not get initial highlight when searching
      const newHighlightIndex =
        newItems.find(item => (matchOption ?? matchOptionDefault)(item.option, newFilterString))
          ?.selectableIndex ?? 0;
      setHighlightedIndex(newHighlightIndex);
    },
    [setFilterStringInState, getItems, matchOption]
  );

  const { items, numTotalRowsIfUnfiltered, selectableIndexMap } = getItems(filterString);
  const rowKeys = useMemo(() => uniqueRowKeys(items), [items]);

  const handleRowsRendered = useCallback(
    (info: { overscanStartIndex: number; overscanStopIndex: number }) => {
      if (onOptionsRendered) {
        const options = items
          .slice(info.overscanStartIndex, info.overscanStopIndex)
          .map(item => item.option);
        onOptionsRendered(options);
      }
    },
    [items, onOptionsRendered]
  );

  const rowVirtualizer = useVirtualizer({
    count: items?.length,
    getScrollElement: () => parentRef.current?.view as Element | null,
    estimateSize: () => optionHeight || 30,
    getItemKey: useCallback((index: number) => rowKeys[index], [rowKeys]),
    overscan: 10,
    rangeExtractor: useCallback(
      (range: Range) => {
        handleRowsRendered({
          overscanStartIndex: range.startIndex,
          overscanStopIndex: range.endIndex + range.overscan,
        });
        return defaultRangeExtractor(range);
      },
      [handleRowsRendered]
    ),
  });

  // Group nodes are not selectable, so don't tell downshift about them
  const numSelectableItems = useMemo(
    () => items.reduce((sum, item) => sum + (item.selectable ? 1 : 0), 0),
    [items]
  );

  const currentItem = useMemo(
    () => items.find(item => item.option.value === value) || null,
    [items, value]
  );
  const currentItemName = (currentItem && currentItem.option.label) || '';

  const onInputFocus = useCallback(() => {
    setHasFocus(true);
    if (!regularInput) {
      setFilterString('');
    } else {
      setFilterString(currentItem?.option.label ?? '');
    }
    setIsOpen(true);
  }, [regularInput, currentItem, setFilterString, setIsOpen, setHasFocus]);
  const onInputBlur = useCallback(() => {
    setHasFocus(false);
  }, [setHasFocus]);
  const onInputClick = useCallback(() => {
    if (!isOpen) {
      setIsOpen(true);
    }
  }, [isOpen, setIsOpen]);
  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFilterString(e.target.value);
    },
    [setFilterString]
  );

  const inputValue = hasFocus ? filterString : currentItemName;
  const hasFocusAndCurrentItem = hasFocus && currentItem != null && !regularInput;
  const inputPlaceholder = hasFocusAndCurrentItem ? currentItemName : placeholder;

  const handleStateChange = useCallback(
    (changes: StateChangeOptions<RowNode<unknown, T>>) => {
      if (changes.inputValue != null && changes.type === stateChangeTypes.changeInput) {
        setFilterString(changes.inputValue);
      }
      if (changes.type === stateChangeTypes.keyDownEnter) {
        if (!isMultiSelect && !regularInput) {
          setFilterString('');
        }
      }
      if (typeof changes.selectedItem !== 'undefined') {
        if (regularInput) {
          setFilterString(changes.selectedItem?.option.label ?? '');
        } else if (!isMultiSelect) {
          setFilterString('');
        }
      }

      if (typeof changes.isOpen === 'boolean' && changes.type !== stateChangeTypes.unknown) {
        const nonClosingChanges: Array<typeof changes.type> = [
          stateChangeTypes.clickItem,
          stateChangeTypes.keyDownEnter,
        ];
        const keepOpen = isMultiSelect && nonClosingChanges.includes(changes.type);

        if (keepOpen && !changes.isOpen) {
          // Do not close
          return {
            ...changes,
            isOpen: true,
          };
        } else {
          setIsOpen(changes.isOpen);
        }
      }

      if (changes.highlightedIndex != null) {
        setHighlightedIndex(changes.highlightedIndex);

        if (
          changes.type === stateChangeTypes.keyDownArrowDown ||
          changes.type === stateChangeTypes.keyDownArrowUp
        ) {
          const realIndex = selectableIndexMap[changes.highlightedIndex];
          if (realIndex) {
            rowVirtualizer.scrollToIndex(realIndex);
          } else {
            debug.warn(`Could not find index ${changes.highlightedIndex} in selectableIndexMap`);
          }
        }
      }

      return changes;
    },
    [selectableIndexMap, isMultiSelect, regularInput, rowVirtualizer, setFilterString, setIsOpen]
  );

  const handleChange = useCallback(
    (newSelectedItem: RowNode<V, T> | null) => {
      const option = (newSelectedItem && newSelectedItem.option) || null;
      const newValue = option && option.value;
      onChange(newValue == null ? null : newValue, option, {});
    },
    [onChange]
  );

  const onInputKeyDown = useCallback(
    (e: React.KeyboardEvent<any>) => {
      if (e.key === 'Escape' && stopEscPropagation) {
        e.stopPropagation();
      }

      if (clearable && e.key === 'Backspace' && currentItem != null && filterString.length === 0) {
        handleChange(null);
      }
    },
    [clearable, currentItem, filterString.length, handleChange, stopEscPropagation]
  );

  return (
    <Downshift
      itemToString={itemToString}
      itemCount={numSelectableItems}
      onStateChange={handleStateChange}
      onChange={handleChange}
      inputValue={filterString}
      selectedItem={currentItem}
      isOpen={isOpen && !disabled}
      highlightedIndex={highlightedIndex}
      scrollIntoView={() => {}}
    >
      {downshift => {
        const rowRenderer = ({ index }: RowRendererProps) => {
          const item = items[index];
          const rowStyle = { lineHeight: `${optionHeight + 1}px` };
          const isIndented = item.level > 0;
          const isSelected =
            item === currentItem || !!(values && values.includes(item.option.value));
          const hasSelectedChild =
            !!item.option.children &&
            item.option.children.some(child => {
              return value === child.value || (values && values.includes(child.value));
            });
          const isHighlighted =
            item.selectable && downshift.highlightedIndex === item.selectableIndex;
          const isDisabled = item.option.disabled;

          const className = classNames(styles.row, {
            [styles.disabled]: isDisabled,
            [styles.highlighted]: !isDisabled && isHighlighted,
            [styles.selected]: !isDisabled && isSelected && highlightSelection,
            [styles.indented]: isIndented,
            [styles.group]: item.level === 0 && !item.selectable,
          });

          const renderOptionMeta = {
            isChild: isIndented,
            isHighlighted,
            isSelected,
            hasSelectedChild,
          };

          let downshiftProps = { style: rowStyle };
          // Only call itemProps() if it's a selectable item,
          // the itemProps() call is not pure
          if (item.selectable) {
            downshiftProps = downshift.getItemProps({
              index: item.selectableIndex,
              disabled: item.option.disabled,
              item,
              ...downshiftProps,
            });
          }

          return (
            <div
              data-testid="search_results"
              className={className}
              title={getOptionTitle?.(item.option) || itemToString(item)}
              {...downshiftProps}
            >
              {renderOptionWithFallback(item.option, index, renderOptionMeta)}
            </div>
          );
        };

        const handleClear = (e: React.SyntheticEvent) => {
          e.preventDefault();
          e.stopPropagation();
          if (filterString.length > 0 && !regularInput) {
            setFilterString('');
          } else {
            downshift.clearSelection();
          }
          onClear?.();
        };

        const isClearable =
          !inputValidationError && ((clearable && !!currentItem) || filterString.length > 0);
        let rightIcon: React.ReactElement | null = null;
        if (inputValidationError) {
          rightIcon = <WarningDiamond className={inputStyles.validationErrorIcon} />;
        } else if (!isClearable) {
          if (inputActions) {
            rightIcon = (
              <div onClick={e => e.stopPropagation()} className={styles.inputActions}>
                {inputActions}
              </div>
            );
          } else if (inputCaret) {
            rightIcon = (
              <button
                {...downshift.getToggleButtonProps()}
                tabIndex={-1}
                disabled={disabled}
                className={classNames(styles.rightIcon, { [styles.disabledButtonHover]: disabled })}
              >
                <FontAwesome
                  name="caret-down"
                  className={classNames(styles.toggleIcon, { [styles.toggleIconActive]: isOpen })}
                />
              </button>
            );
          }
        }

        const renderInput = (props?: RenderInputProps) => {
          const inputProps = downshift.getInputProps({
            // Chrome disables autocomplete when passed unknown autoComplete attributes
            placeholder: inputPlaceholder,
            ...props,
            size: props && typeof props.size === 'number' ? props.size : undefined,
            onFocus: chainEvent(onInputFocus, props && props.onFocus),
            onBlur: chainEvent(onInputBlur, props && props.onBlur),
            onKeyDown: chainEvent(onInputKeyDown, props && props.onKeyDown),
            onClick: chainEvent(onInputClick, props && props.onClick),
            onChange: chainEvent(onInputChange, props && props.onChange),
            className: classNames(props && props.className, styles.input, {
              [styles.focusedInput]: hasFocusAndCurrentItem,
              [styles.hasIcon]: inputIcon != null || isLoadingOptions,
            }),
          });

          const prefix = isLoadingOptions ? (
            <div className={styles.loadingIcon}>
              <Spinner scale={0.4} />
            </div>
          ) : inputIcon ? (
            <div className={styles.inputIcon}>{inputIcon}</div>
          ) : undefined;

          const mergedInputRef =
            typeof inputProps.ref !== 'string' ? mergeRefs(inputProps.ref, inputRef) : inputRef;

          return (
            <>
              <InputDecorator
                prefix={prefix}
                suffix={rightIcon}
                isClearable={isClearable}
                onClear={handleClear}
                inline={inlineInput}
              >
                <InputWithAutoFillBlocking
                  disabled={disabled}
                  {...inputProps}
                  value={inputValue}
                  /* Fix weird type of getInputProps().ref */
                  ref={mergedInputRef}
                  hasValidationError={!!inputValidationError}
                />
              </InputDecorator>
              {!!inputValidationError && (
                <div className={inputStyles.validationErrorText}>{inputValidationError}</div>
              )}
            </>
          );
        };

        const renderList = ({ width = 350, position, className }: RenderListProps = {}) => {
          let height = optionHeight * Math.min(15, Math.max(1, numTotalRowsIfUnfiltered));
          if (position != null && position.startsWith('bottom')) {
            // Shrink dropdown during search, but only if popover
            // is positioned on bottom. For other positions,
            // shrinking would look weird
            height = optionHeight * Math.min(15, Math.max(1, items.length));
          }

          //  https://www.a11yproject.com/posts/how-to-use-the-tabindex-attribute/#:~:text=focus()%20method.-,Scrollable%20overflow%20containers,-tabindex%3D%220%22
          parentRef.current?.view?.setAttribute('tabindex', '-1');

          return items.length > 0 ? (
            <div {...downshift.getMenuProps()} className={styles.listWrapper}>
              <Scrollbars
                ref={parentRef}
                style={{ height: maxHeight && height > maxHeight ? maxHeight : height, width }}
                className={className}
              >
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map(({ index, key, size, start }) => {
                    return (
                      <div
                        key={key}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: `${size}px`,
                          transform: `translateY(${start}px)`,
                        }}
                      >
                        {rowRenderer({ index, key })}
                      </div>
                    );
                  })}
                </div>
              </Scrollbars>
            </div>
          ) : (
            <div
              data-testid="no_search_results"
              className={styles.noResults}
              style={{ minHeight: height, width }}
            >
              {noResultsLabel}
            </div>
          );
        };

        const renderListPopover = ({
          popoverPosition,
          renderListProps = {},
          popoverListHeader,
          usePortal,
        }: RenderListPopoverProps = {}) => {
          const targetStyle: {
            [css: string]: unknown;
          } = {
            position: 'absolute',
            zIndex: -1,
            top: 8,
            bottom: 8,
            left: 0,
            right: 0,
          };

          return (
            <Popover
              usePortal={usePortal}
              fixed={popoverFixed}
              enableKeyTrap={false} // dealt with internally, see onInputKeyDown
              closeOnOutsideClick={false} // Downshift takes care of this
              isOpen={isOpen}
              position={popoverPosition ?? 'bottom-start'}
              width="auto"
              target={<div style={targetStyle} />}
              targetWrapper={false}
              className={styles.popover}
            >
              {position => (
                <div>
                  {popoverListHeader && popoverListHeader}
                  {renderList({ position, ...renderListProps })}
                </div>
              )}
            </Popover>
          );
        };

        const renderTags = ({ suggestedTags, selectedTag, onClickTag }: RenderTagsProps = {}) => {
          if (!suggestedTags?.length && !selectedTag) return null;

          return (
            <HourTags tags={suggestedTags} onClickTag={onClickTag} selectedTag={selectedTag} />
          );
        };

        const rendered = children || defaultLayout;

        return rendered({
          downshift,
          renderInput,
          renderList,
          renderListPopover,
          renderTags,
          isOpen,
          hasItems: items.length > 0,
        });
      }}
    </Downshift>
  );
};

const defaultProps = {
  placeholder: 'Search...',
  optionHeight: 30,
  renderOption: defaultOptionRenderer,
  children: defaultLayout,
  inputCaret: true,
  stopEscPropagation: true,
  popoverFixed: false,
  rootOptionIsGroup: false,
  highlightSelection: true,
  minimumCharactersToSearchParent: 3,
};

DropdownBuilder.defaultProps = defaultProps;

export { defaultProps };

export default DropdownBuilder;

const processOptions = <V, T extends DropdownOption<V>>(
  options: ReadonlyArray<T>,
  filterString: string,
  matchOptionFromProps: ((option: T, filterString: string) => boolean) | null | undefined,
  isMultiSelect: boolean,
  rootOptionIsGroup: boolean = true,
  minimumCharactersToSearchParent: number = 3,
  postProcessOptions: (
    options: Array<RowNode<V, T>>,
    filterString: string,
    addOption: (option: T) => void
  ) => void = () => {}
): {
  items: Array<RowNode<V, T>>;
  numTotalRowsIfUnfiltered: number;
  selectableIndexMap: Record<number, number>;
} => {
  const matchOption = matchOptionFromProps || matchOptionDefault;

  let filtered: ReadonlyArray<T> = options;
  let numTotalRowsIfUnfiltered = 0;
  if (filterString.length > 0) {
    const newFiltered: Array<T> = [];
    options.forEach((option: T) => {
      if (option.children) {
        numTotalRowsIfUnfiltered += option.children.length + 1;
        // @ts-expect-error
        const filteredChildren = option.children.filter(c => matchOption(c, filterString));
        const matchesParent =
          filterString.length >= minimumCharactersToSearchParent &&
          matchOption(option, filterString);
        const matchesChildren = filteredChildren.length > 0;
        // If search matches parent with min 3 chars, but none of it's children,
        // include full group. May potentially want to hide this behind a prop?
        if (matchesParent && !matchesChildren) {
          newFiltered.push(option);
        } else if (filteredChildren.length > 0) {
          newFiltered.push({
            ...option,
            children: filteredChildren,
          });
        }
      } else if (matchOption(option, filterString)) {
        numTotalRowsIfUnfiltered += 1;
        newFiltered.push(option);
      }
    });
    filtered = newFiltered;
  }
  const items: Array<RowNode<V, T>> = [];
  filtered.forEach(n => {
    const hasChildren = Array.isArray(n.children) && n.children.length > 0;
    const selectable =
      (!rootOptionIsGroup || !hasChildren) &&
      (isMultiSelect || !hasChildren) &&
      n.isSelectable !== false;

    items.push({
      option: n,
      selectable,
      selectableIndex: 0,
      level: 0,
    });
    if (n.children) {
      // Identity of the parent group; lets children keep stable keys per group. (TT-5200)
      const parentKey =
        n.rowKey ?? (n.value != null && typeof n.value !== 'object' ? String(n.value) : undefined);
      n.children.forEach(c => {
        items.push({
          option: c as T,
          selectable: true,
          selectableIndex: 0,
          level: 1,
          parentKey,
        });
      });
    }
  });
  if (numTotalRowsIfUnfiltered < 1) {
    numTotalRowsIfUnfiltered = items.length;
  }

  postProcessOptions(items, filterString, option => {
    items.push({
      option,
      selectable: option.isSelectable ?? true,
      selectableIndex: 0,
      level: 0,
    });
  });

  /**
   * Maps the actual item indices that rowVirtualizer sees to the selectable indices, so that
   * scrollToIndex works as expected.
   */
  const selectableIndexMap: Record<number, number> = {};

  let n = 0;
  items.forEach((item, i) => {
    if (item.selectable) {
      item.selectableIndex = n++;
      selectableIndexMap[item.selectableIndex] = i;
    }
  });

  return { items, numTotalRowsIfUnfiltered, selectableIndexMap };
};
